import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Table, 
  Tag, 
  Button, 
  Dropdown, 
  Menu, 
  Typography, 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Input, 
  Select, 
  Space, 
  Avatar, 
  Badge, 
  Tooltip, 
  message,
  Empty,
  Spin
} from 'antd';
import { 
  EllipsisOutlined, 
  SearchOutlined, 
  UserOutlined, 
  EyeOutlined, 
  EditOutlined, 
  DeleteOutlined,
  FilterOutlined,
  ReloadOutlined,
  DownloadOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  TableOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import { getAssignments } from '../../api';
import { useNavigate } from 'react-router-dom';
import './CandidateApplications.css';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const CandidateApplications = () => {
  const [dataSource, setDataSource] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getAssignments();
        const assignments = response.data.map((assignment) => ({
          ...assignment,
          key: assignment._id || assignment.id,
        }));
        setDataSource(assignments);
        setFilteredData(assignments);
        message.success(`Loaded ${assignments.length} candidate applications`);
      } catch (error) {
        console.error('Error fetching assignments:', error);
        message.error('Failed to load candidate applications');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter data based on search and status
  useEffect(() => {
    let filtered = dataSource;

    // Apply search filter
    if (searchText) {
      filtered = filtered.filter(item => 
        item.employee?.fullName?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.employee?.email?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.employee?.phone?.includes(searchText) ||
        item.status?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    setFilteredData(filtered);
  }, [dataSource, searchText, statusFilter]);

  const handleViewDetails = (record) => {
    navigate(`/employer/candidate-detail/${record._id}`, { state: { candidate: record } });
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleExport = () => {
    // Export filtered data to CSV
    const csvContent = "data:text/csv;charset=utf-8," 
      + "Name,Email,Phone,Status,Agency\n"
      + filteredData.map(item => 
          `"${item.employee?.fullName || 'N/A'}","${item.employee?.email || 'N/A'}","${item.employee?.phone || 'N/A'}","${item.status || 'N/A'}","${item.agency || 'N/A'}"`
        ).join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `candidate_applications_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success('Data exported successfully');
  };

  const getStatusColor = (status) => {
    const colors = {
      'idle': 'blue',
      'submitted': 'orange',
      'application-rejected': 'red',
      'offer-rejected': 'red',
      'extend-offer-rejected': 'red',
      'offer-sent': 'purple',
      'extend-offer-sent': 'purple',
      'offer-accepted': 'green',
      'extend-offer-accepted': 'green',
      'ongoing': 'green',
      'compliance-in-progress': 'cyan',
      'ended': 'default',
      'pending': 'orange',
      'accepted': 'green',
      'rejected': 'red'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'accepted': <CheckCircleOutlined />,
      'offer-accepted': <CheckCircleOutlined />,
      'ongoing': <CheckCircleOutlined />,
      'pending': <ClockCircleOutlined />,
      'submitted': <ClockCircleOutlined />,
      'offer-sent': <ClockCircleOutlined />,
      'rejected': <ExclamationCircleOutlined />,
      'application-rejected': <ExclamationCircleOutlined />
    };
    return icons[status] || <UserOutlined />;
  };

  const getUniqueStatuses = () => {
    const statuses = [...new Set(dataSource.map(item => item.status))];
    return statuses.filter(Boolean);
  };

  const getStatusCounts = () => {
    const counts = {};
    dataSource.forEach(item => {
      counts[item.status] = (counts[item.status] || 0) + 1;
    });
    return counts;
  };

  const columns = [
    {
      title: 'Candidate',
      key: 'candidate',
      render: (record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Avatar 
            size={40} 
            icon={<UserOutlined />} 
            style={{ backgroundColor: '#374151' }}
          />
          <div>
            <div style={{ fontWeight: '500', fontSize: '14px' }}>
              {record.employee?.fullName || 'N/A'}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.employee?.email || 'N/A'}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (record) => (
        <div>
          <div style={{ marginBottom: '4px', fontSize: '13px' }}>
            <PhoneOutlined style={{ marginRight: '6px', color: '#6b7280' }} />
            {record.employee?.phone || 'N/A'}
          </div>
          <div style={{ fontSize: '13px' }}>
            <EnvironmentOutlined style={{ marginRight: '6px', color: '#6b7280' }} />
            {record.employee?.address?.city ? 
              `${record.employee.address.city}, ${record.employee.address.state}` : 
              'N/A'
            }
          </div>
        </div>
      ),
    },
    {
      title: 'Skills & Agency',
      key: 'skills',
      render: (record) => (
        <div>
          <div style={{ marginBottom: '8px' }}>
            {record.employee?.units?.slice(0, 2).map((unit, index) => (
              <Tag 
                key={index} 
                size="small" 
                                 style={{ 
                   backgroundColor: '#f3f4f6',
                   color: '#374151',
                   border: '1px solid #d1d5db',
                   borderRadius: '4px',
                   fontSize: '11px'
                 }}
              >
                {unit}
              </Tag>
            ))}
            {record.employee?.units?.length > 2 && (
              <Tag size="small" style={{ fontSize: '11px' }}>
                +{record.employee.units.length - 2} more
              </Tag>
            )}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            <TeamOutlined style={{ marginRight: '4px' }} />
            {record.agency || 'Direct Application'}
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag 
          icon={getStatusIcon(status)}
          color={getStatusColor(status)}
          style={{ 
            borderRadius: '4px',
            padding: '4px 12px',
            fontSize: '12px',
            fontWeight: '500'
          }}
        >
          {status?.replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase()) || 'Unknown'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'action',
      width: 120,
      render: (text, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button 
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
              style={{ 
                borderRadius: '4px',
                backgroundColor: '#374151',
                borderColor: '#374151'
              }}
            />
          </Tooltip>
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item 
                  key="edit" 
                  icon={<EditOutlined />}
                  onClick={() => message.info('Edit functionality coming soon')}
                >
                  Edit Candidate
                </Menu.Item>
                <Menu.Item 
                  key="delete" 
                  icon={<DeleteOutlined />}
                  onClick={() => message.info('Delete functionality coming soon')}
                  danger
                >
                  Delete
                </Menu.Item>
              </Menu>
            }
            trigger={['click']}
          >
            <Button 
              size="small" 
              icon={<EllipsisOutlined />}
              style={{ 
                borderRadius: '4px',
                border: '1px solid #d1d5db'
              }}
            />
          </Dropdown>
        </Space>
      ),
    },
  ];

  const statusCounts = getStatusCounts();

  const renderCardView = () => (
    <Row gutter={[16, 16]}>
      {filteredData.map((candidate) => (
        <Col xs={24} sm={12} lg={8} xl={6} key={candidate.key}>
          <Card
            hoverable
            style={{
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              height: '100%'
            }}
            bodyStyle={{ padding: '16px' }}
            onClick={() => handleViewDetails(candidate)}
          >
            <div style={{ textAlign: 'center', marginBottom: '12px' }}>
              <Avatar 
                size={60} 
                icon={<UserOutlined />} 
                style={{ backgroundColor: '#374151', marginBottom: '8px' }}
              />
              <div style={{ fontWeight: '600', fontSize: '16px', marginBottom: '4px' }}>
                {candidate.employee?.fullName || 'N/A'}
              </div>
              <Tag 
                icon={getStatusIcon(candidate.status)}
                color={getStatusColor(candidate.status)}
                style={{ borderRadius: '4px', fontSize: '11px' }}
              >
                {candidate.status?.replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}
              </Tag>
            </div>
            
            <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
              <div style={{ marginBottom: '6px' }}>
                <MailOutlined style={{ color: '#6b7280', marginRight: '6px' }} />
                {candidate.employee?.email || 'N/A'}
              </div>
              <div style={{ marginBottom: '6px' }}>
                <PhoneOutlined style={{ color: '#6b7280', marginRight: '6px' }} />
                {candidate.employee?.phone || 'N/A'}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <TeamOutlined style={{ color: '#6b7280', marginRight: '6px' }} />
                {candidate.agency || 'Direct Application'}
              </div>
              
              {candidate.employee?.units?.length > 0 && (
                <div>
                  {candidate.employee.units.slice(0, 2).map((unit, index) => (
                    <Tag 
                      key={index} 
                      size="small" 
                      style={{ 
                        backgroundColor: '#f3f4f6',
                        color: '#374151',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '10px',
                        margin: '2px'
                      }}
                    >
                      {unit}
                    </Tag>
                  ))}
                  {candidate.employee.units.length > 2 && (
                    <Tag size="small" style={{ fontSize: '10px', margin: '2px' }}>
                      +{candidate.employee.units.length - 2}
                    </Tag>
                  )}
                </div>
              )}
            </div>
          </Card>
        </Col>
      ))}
    </Row>
  );

  if (loading) {
    return (
      <Content style={{ padding: '24px', minHeight: '80vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spin size="large" tip="Loading candidate applications..." />
      </Content>
    );
  }

  return (
    <div style={{ padding: '20px', background: '#fff', minHeight: '100vh',borderRadius: '8px' }}>
      {/* Header Section */}
      <Card 
        style={{ 

          backgroundColor: '#ffffff',
          border: 'none',
        }}
        bodyStyle={{ padding: '24px' }}
      >
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={2} style={{ margin: 0, color: '#1f2937', fontWeight: '600' }}>
              Candidate Applications
            </Title>
            <Text style={{ color: '#6b7280', fontSize: '16px' }}>
              Manage and review all candidate applications
            </Text>
          </Col>
          <Col>
            <Space>
              <Button 
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                style={{ 
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  backgroundColor: '#ffffff'
                }}
              >
                Refresh
              </Button>
              <Button 
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleExport}
                disabled={filteredData.length === 0}
                style={{ 
                  borderRadius: '6px',
                  backgroundColor: '#374151',
                  borderColor: '#374151'
                }}
              >
                Export ({filteredData.length})
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={12} sm={6}>
          <Card 
            style={{ 
              textAlign: 'center',
              borderRadius: '8px',
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
            bodyStyle={{ padding: '24px 16px' }}
          >
            <Statistic
              title={<span style={{ color: '#6b7280', fontSize: '14px', fontWeight: '500' }}>Total Applications</span>}
              value={dataSource.length}
              valueStyle={{ color: '#1f2937', fontSize: '32px', fontWeight: '700' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card 
            style={{ 
              textAlign: 'center',
              borderRadius: '8px',
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
            bodyStyle={{ padding: '24px 16px' }}
          >
            <Statistic
              title={<span style={{ color: '#6b7280', fontSize: '14px', fontWeight: '500' }}>Pending Review</span>}
              value={statusCounts['pending'] || statusCounts['submitted'] || 0}
              valueStyle={{ color: '#d97706', fontSize: '32px', fontWeight: '700' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card 
            style={{ 
              textAlign: 'center',
              borderRadius: '8px',
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
            bodyStyle={{ padding: '24px 16px' }}
          >
            <Statistic
              title={<span style={{ color: '#6b7280', fontSize: '14px', fontWeight: '500' }}>Accepted</span>}
              value={statusCounts['accepted'] || statusCounts['offer-accepted'] || statusCounts['ongoing'] || 0}
              valueStyle={{ color: '#059669', fontSize: '32px', fontWeight: '700' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card 
            style={{ 
              textAlign: 'center',
              borderRadius: '8px',
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
            bodyStyle={{ padding: '24px 16px' }}
          >
            <Statistic
              title={<span style={{ color: '#6b7280', fontSize: '14px', fontWeight: '500' }}>Filtered Results</span>}
              value={filteredData.length}
              valueStyle={{ color: '#374151', fontSize: '32px', fontWeight: '700' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters and Controls */}
      <Card 
        style={{ 
          marginBottom: '24px', 
          borderRadius: '8px',
          backgroundColor: '#ffffff',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}
        bodyStyle={{ padding: '20px' }}
      >
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder="Search candidates..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ borderRadius: '6px' }}
              prefix={<SearchOutlined style={{ color: '#6b7280' }} />}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              style={{ width: '100%', borderRadius: '6px' }}
              placeholder="Filter by status"
              value={statusFilter}
              onChange={setStatusFilter}
              suffixIcon={<FilterOutlined style={{ color: '#6b7280' }} />}
            >
              <Option value="all">All Statuses ({dataSource.length})</Option>
              {getUniqueStatuses().map(status => (
                <Option key={status} value={status}>
                  {status?.replace(/-/g, ' ').replace(/\b\w/g, char => char.toUpperCase())} ({statusCounts[status]})
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Button.Group style={{ width: '100%' }}>
              <Button
                type={viewMode === 'table' ? 'primary' : 'default'}
                icon={<TableOutlined />}
                onClick={() => setViewMode('table')}
                style={{ 
                  width: '50%',
                  borderRadius: '4px 0 0 4px',
                  backgroundColor: viewMode === 'table' ? '#374151' : '#ffffff',
                  borderColor: viewMode === 'table' ? '#374151' : '#d1d5db',
                  color: viewMode === 'table' ? '#ffffff' : '#374151'
                }}
              >
                Table
              </Button>
              <Button
                type={viewMode === 'cards' ? 'primary' : 'default'}
                icon={<AppstoreOutlined />}
                onClick={() => setViewMode('cards')}
                style={{ 
                  width: '50%',
                  borderRadius: '0 4px 4px 0',
                  backgroundColor: viewMode === 'cards' ? '#374151' : '#ffffff',
                  borderColor: viewMode === 'cards' ? '#374151' : '#d1d5db',
                  color: viewMode === 'cards' ? '#ffffff' : '#374151'
                }}
              >
                Cards
              </Button>
            </Button.Group>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Text type="secondary" style={{ fontSize: '14px', color: '#6b7280' }}>
              Showing {filteredData.length} of {dataSource.length} applications
            </Text>
          </Col>
        </Row>
      </Card>

      {/* Main Content */}
      <Card 
        style={{ 
          borderRadius: '8px',
          backgroundColor: '#ffffff',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}
        bodyStyle={{ padding: viewMode === 'cards' ? '20px' : '0' }}
      >
        {filteredData.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span>
                {searchText || statusFilter !== 'all' 
                  ? 'No candidates match your search criteria' 
                  : 'No candidate applications found'
                }
              </span>
            }
            style={{ padding: '60px 20px' }}
          >
            {(searchText || statusFilter !== 'all') && (
              <Button 
                type="primary" 
                onClick={() => {
                  setSearchText('');
                  setStatusFilter('all');
                }}
                style={{ 
                  borderRadius: '4px',
                  backgroundColor: '#374151',
                  borderColor: '#374151'
                }}
              >
                Clear Filters
              </Button>
            )}
          </Empty>
        ) : viewMode === 'table' ? (
          <Table
            dataSource={filteredData}
            columns={columns}
            pagination={{ 
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} candidates`,
            }}
            scroll={{ x: 800 }}
            style={{
              '.ant-table-thead > tr > th': {
                backgroundColor: '#fafafa',
                fontWeight: '600',
                borderRadius: '0'
              }
            }}
          />
        ) : (
          renderCardView()
        )}
      </Card>
    </div>
  );
};

export default CandidateApplications;
